import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Sparkles, Camera, Flag } from 'lucide-react';
import api, { RARITY_META } from '../lib/api';

export default function Regions() {
  const [areas, setAreas] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/regions/completion').then(r => setAreas(r.data.areas || [])).catch(() => []).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setDetail(null);
    api.get(`/regions/completion?area=${encodeURIComponent(selected)}`).then(r => setDetail(r.data));
  }, [selected]);

  return (
    <div className="space-y-5" data-testid="regions-root">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Regional Completion</p>
        <h2 className="font-serif text-3xl">Explore by area</h2>
        <p className="text-xs text-muted-foreground mt-1">Every area you visit shows your % completion vs. everything the community has ever recorded there.</p>
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : areas.length === 0 ? (
        <div className="rounded-3xl bg-card border border-white/5 p-8 text-center">
          <MapPin className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-serif text-lg">No regions yet</p>
          <p className="text-xs text-muted-foreground mt-1">Make a GPS-tagged discovery to unlock your first region.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {areas.map(a => (
            <button
              key={a.area}
              type="button"
              onClick={() => setSelected(a.area)}
              data-testid="region-row"
              className={`rounded-2xl bg-card border p-4 text-left transition-colors ${
                selected === a.area ? 'border-accent/60' : 'border-white/5 hover:border-white/20'
              }`}
            >
              <div className="flex items-center gap-3">
                <ProgressRing pct={a.completion_pct} />
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-base truncate">{a.area}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {a.user_species} / {a.area_species} species · {a.observations} sightings
                  </p>
                </div>
                <p className="font-mono text-lg tabular-nums text-accent">{a.completion_pct}%</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && detail && (
        <motion.div initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="rounded-3xl bg-card border border-accent/30 p-5 space-y-4"
          data-testid="region-detail"
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Detail</p>
              <h3 className="font-serif text-2xl truncate">{detail.area}</h3>
              <p className="text-[10px] text-muted-foreground">{detail.user_species} of {detail.area_species} species observed</p>
            </div>
            <ProgressRing pct={detail.overall_completion_pct} size="lg" />
          </div>

          <div className="space-y-3">
            {detail.categories.filter(c => c.total > 0).map(c => (
              <CategoryBlock key={c.category} category={c} />
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}

function CategoryBlock({ category }) {
  return (
    <div className="rounded-2xl bg-black/30 border border-white/5 p-3" data-testid="region-category">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm">{category.category}</p>
        <p className="text-xs font-mono text-accent">{category.seen}/{category.total} · {category.completion_pct}%</p>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <motion.div
          initial={{ width: 0 }} animate={{ width: `${category.completion_pct}%` }} transition={{ duration: 0.6 }}
          className="h-full bg-accent/70"
        />
      </div>
      {category.missing.length > 0 && (
        <div className="mt-3">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">Missing (silhouettes)</p>
          <div className="grid grid-cols-4 gap-1.5" data-testid="missing-silhouettes">
            {category.missing.slice(0, 8).map(s => (
              <div key={s.species_id} className="rounded-lg aspect-square bg-neutral-900 border border-white/5 relative overflow-hidden flex items-center justify-center">
                <div className="absolute inset-0 flex items-center justify-center">
                  <Camera className="w-5 h-5 text-neutral-700" strokeWidth={1.5} />
                </div>
                <span className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full" style={{ background: RARITY_META[s.rarity]?.color || '#666' }} />
                <span className="absolute inset-x-0 bottom-0 text-[8px] px-1 pb-0.5 bg-gradient-to-t from-black/70 to-transparent truncate">?</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProgressRing({ pct, size = 'md' }) {
  const px = size === 'lg' ? 64 : 42;
  const stroke = size === 'lg' ? 6 : 4;
  const r = (px - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={px} height={px} className="flex-shrink-0" data-testid="progress-ring">
      <circle cx={px/2} cy={px/2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} fill="none" />
      <motion.circle
        cx={px/2} cy={px/2} r={r}
        stroke="#E9C46A" strokeWidth={stroke} fill="none" strokeLinecap="round"
        strokeDasharray={c}
        initial={{ strokeDashoffset: c }}
        animate={{ strokeDashoffset: c - (c * pct / 100) }}
        transition={{ duration: 0.9, ease: 'easeOut' }}
        transform={`rotate(-90 ${px/2} ${px/2})`}
      />
      <text x={px/2} y={px/2} textAnchor="middle" dominantBaseline="central"
        className="font-mono" fontSize={size === 'lg' ? 14 : 10} fill="#E9C46A">{pct}%</text>
    </svg>
  );
}
